import { Component, input, OnDestroy, OnInit, output } from '@angular/core';
import { PosMarketSearchParameter, VALIDATORS } from '../../../service/model/pos';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MarketElement } from '../../../service/model/market/market-element';
import { select, Store } from '@ngrx/store';
import { SetMarketsSearchValueAction } from '../../../store/markets/markets-actions';
import { selectMarketsSearchValue } from '../../../store/markets/markets-selector';
import { UserDetailsService } from '../../../service/user-details.service';
import { SearchCriteriaComponent } from '../../../base/search-criteria/search-criteria.component';
import { UppValidatorService } from '../../../service/upp-validator.service';
import { UppComponentNames } from '../../../service/model';
import { LookupOptions } from '../../../core/util/lookup-options';
import { selectNavigation } from '../../../store/navigation/navigation-selector';
import { NgClass } from '@angular/common';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { LoadingAlertComponent } from '../../../components/loading-alert/loading-alert.component';
import { MultiSelectBadgesComponent } from '../../../components/multi-select-badges/multi-select-badges.component';
import { MarketsState } from '../../../store/markets/markets-state';
import { ConfigurationService } from '../../../service/configuration/configuration.service';
import { FeatureFlags } from '../../../core/util/resources';
import { alphanumericSpaceMax30 } from '../../../service/model/common/validators';
import { Subscription } from 'rxjs';

@Component({
  selector: 'ama-ng-upp-markets-search-criteria',
  templateUrl: './markets-search-criteria.component.html',
  styleUrls: ['./markets-search-criteria.component.scss'],
  standalone: true,
  imports: [LoadingAlertComponent, ReactiveFormsModule, NgbTooltip, NgClass, MultiSelectBadgesComponent]
})
export class MarketsSearchCriteriaComponent extends SearchCriteriaComponent implements OnInit, OnDestroy {
  sendingStatus = input<boolean>(false);

  searchMarkets = output<PosMarketSearchParameter>();
  clearMarkets = output<boolean>();

  readonly COMPONENT_NAME = UppComponentNames.MARKETS;

  readonly = false;
  nameRegex = '^[A-Z0-9]{0,30}$';

  numberOfItems = 2;

  marketsElement: MarketElement = { location: {} };
  searchParameters!: PosMarketSearchParameter;

  regionPasteRegExp = /[,|;]/; // separator regex for ',' and ';'

  lookupOptions?: LookupOptions;
  lookup?: boolean = false;

  spacesAllowed = false;
  private readonly subscription = new Subscription();

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly store: Store<MarketsState>,
    private readonly userDetailsService: UserDetailsService,
    private readonly validatorService: UppValidatorService,
    private readonly configurationService: ConfigurationService
  ) {
    super();
    this.validatorService.setComponentValidation(UppComponentNames.MARKETS, VALIDATORS);
    this.buildRegionsDescriptionOnly();

    this.subscription.add(
      this.configurationService.getParameter$(FeatureFlags.spacesAllowedInPosAndMarkets).subscribe((isEnabled) => {
        this.spacesAllowed = isEnabled;
        this.nameRegex = isEnabled ? alphanumericSpaceMax30 : '^[A-Z0-9]{0,30}$';
        this.searchCriteriaForm
          ?.get('name')
          ?.setValidators([Validators.maxLength(30), Validators.pattern(this.nameRegex)]);
        this.searchCriteriaForm?.get('name')?.updateValueAndValidity();
      })
    );
  }

  get includeFormGroup(): UntypedFormGroup {
    return this.searchCriteriaForm?.get('include') as UntypedFormGroup;
  }

  ngOnInit(): void {
    this.createAndLoadSearchForm();
    this.subscription.add(
      this.store.select(selectNavigation).subscribe((lookupParams) => (this.lookupOptions = lookupParams))
    );
    this.lookup = this.lookupOptions?.lookup;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  createAndLoadSearchForm() {
    this.searchCriteriaForm = this.formBuilder.group({
      name: this.formBuilder.control('', [Validators.maxLength(30), Validators.pattern(this.nameRegex)]),
      include: this.formBuilder.group({
        world: this.formBuilder.control(false)
      })
    });
    setTimeout(() => {
      this.subscription.add(
        this.store.pipe(select(selectMarketsSearchValue)).subscribe((markets) => {
          if (!this.lookup && markets) {
            this.searchCriteriaForm?.patchValue(markets);
          }
        })
      );
    }, 0);
  }

  clearClicked() {
    this.searchCriteriaForm?.setValue({
      name: '',
      include: {
        world: false,
        regions: [],
        countries: [],
        cities: [],
        airports: []
      }
    });
    this.clearMarkets.emit(true);
  }

  searchClicked() {
    const regionCodes = this.getRegionCodesForDescriptions(this.searchCriteriaForm?.get(['include', 'regions'])?.value);
    this.marketsElement = {
      location: {
        world: this.searchCriteriaForm?.get(['include', 'world'])?.value,
        regions: regionCodes,
        countries: this.searchCriteriaForm?.get(['include', 'countries'])?.value,
        cities: this.searchCriteriaForm?.get(['include', 'cities'])?.value,
        airports: this.searchCriteriaForm?.get(['include', 'airports'])?.value
      }
    };
    this.initializeSearchParameters();
  }

  initializeSearchParameters() {
    if (this.userDetailsService.loggedInUser?.partitions) {
      this.searchParameters = {
        partitionId: this.userDetailsService.loggedInUser.selectedPartition,
        element: this.marketsElement
      };
    } else {
      this.searchParameters = {
        organization: this.userDetailsService.getLssOrganization(),
        element: this.marketsElement
      };
    }
    const name = this.searchCriteriaForm?.get('name')?.value as string;
    if (name && name.length > 0) {
      this.searchParameters.name = name;
    }
    this.emitSearchParameters();
  }

  // Search-parameters(form value) will be emitted to the MarketsSearchComponent, in order to create a search request.
  emitSearchParameters() {
    this.searchMarkets.emit(this.searchParameters);
  }

  storeUnsavedMarketsSearch() {
    this.store.dispatch(new SetMarketsSearchValueAction({ value: this.searchCriteriaForm?.value }));
  }
}
